import { call, put, select, takeEvery } from "redux-saga/effects";

import { BILLING_DOCUMENT, REPORT_JOB } from "constants/actionTypes";
import {
  BillingDocumentReportJobStatus,
  BillingDocumentType,
} from "constants/billingDocuments";

import { fetchWithFullyQualifiedUrl } from "lib/fetch";
import toast from "lib/toast";

import {
  getActiveRole,
  getBillingDocumentById,
  getBusinesses,
} from "selectors";
import { ReportJobAction } from "actions/reportJob";
import { takeActionOutcome } from "./lib";
import {
  BulkExportReportJobFailureAction,
  BulkExportReportJobSuccessAction,
} from "actions/reportJob.compile";

export async function fetchBillingDocumentReportJobs(
  billingDocumentIds: string[],
  userRoleSlug: string,
) {
  const reportJobIds: string[] = [];
  const ineligibleBillingDocumentIds: string[] = [];

  const responses = await Promise.all(
    billingDocumentIds.map(billingDocumentId =>
      fetchWithFullyQualifiedUrl(
        `/v2/billing-documents/${billingDocumentId}/report-job/?document_status=${BillingDocumentReportJobStatus.COMMITTED}&user_role=${userRoleSlug}`,
        { headers: { Accept: "application/json" } },
      ),
    ),
  );

  await Promise.all(
    responses.map(async (response, index) => {
      if (response.ok) {
        const reportJob = await response.json();
        reportJobIds.push(reportJob.id);
      } else {
        ineligibleBillingDocumentIds.push(billingDocumentIds[index]);
      }
    }),
  );

  return { reportJobIds, ineligibleBillingDocumentIds };
}

type CompileBillingDocumentsAction = {
  type: typeof BILLING_DOCUMENT.COMPILE_DOCUMENTS.ACTION;
  billingDocumentIds: string[];
};

function* onCompileBillingDocuments(action: CompileBillingDocumentsAction) {
  const { billingDocumentIds } = action;

  const state: State = yield select();

  const userRoleSlug: string = getActiveRole(state).slug;
  const businessByIdLookup: StateSlice<Business> = getBusinesses(state);

  const { reportJobIds, ineligibleBillingDocumentIds } = yield call(
    fetchBillingDocumentReportJobs,
    billingDocumentIds,
    userRoleSlug,
  );

  if (ineligibleBillingDocumentIds.length > 0) {
    const ineligibleBillingDocumentNames = ineligibleBillingDocumentIds
      .map((billingDocumentId: string): BillingDocument | null =>
        getBillingDocumentById(billingDocumentId)(state),
      )
      .filter(Boolean)
      .map((billingDocument: BillingDocument): string => {
        const businessId =
          billingDocument.type === BillingDocumentType.RCTI
            ? billingDocument.recipientBusinessId
            : billingDocument.issuerBusinessId;

        return billingDocument.number
          ? `${billingDocument.type} ${billingDocument.number}`
          : `${billingDocument.type} for ${businessByIdLookup[businessId]?.name}`;
      })
      .join(", ");

    toast.error(
      `The following documents are not ready to be compiled: ${ineligibleBillingDocumentNames}`,
    );

    return;
  }

  yield put(ReportJobAction.bulkExport(reportJobIds));

  yield takeActionOutcome(
    REPORT_JOB.BULK_EXPORT.SUCCESS,
    REPORT_JOB.BULK_EXPORT.FAILURE,
    (compileSucceededAction: BulkExportReportJobSuccessAction) => {
      if (compileSucceededAction.successUrl) {
        window.open(compileSucceededAction.successUrl, "_blank");
      }
    },
    (compileFailedAction: BulkExportReportJobFailureAction) => {
      toast.error(
        `Bulk export responded with code: ${compileFailedAction.status} (${compileFailedAction.statusText})`,
      );
    },
  );
}

export default function* billingDocumentsCompileSaga() {
  yield takeEvery(
    BILLING_DOCUMENT.COMPILE_DOCUMENTS.ACTION,
    onCompileBillingDocuments,
  );
}
